/**
 * Placing tasks on a time axis.
 *
 * Neither the list nor the board answers "what is happening between now and the
 * end of the operational period, and does it overlap". Both dates are already
 * on every action, so the only new thing needed is the arithmetic that turns a
 * start and a target into a bar, and that is all this module is.
 *
 * Everything is expressed as fractions of the window rather than pixels, so the
 * component can render at any width and the maths stays testable.
 */

import dayjs from 'dayjs';
import { isActionOverdue } from './actionEditModel';

export const TIMELINE_RANGES = ['day', 'week', 'month'] as const;

export type TimelineRange = (typeof TIMELINE_RANGES)[number];

export const TIMELINE_RANGE_LABELS: Record<TimelineRange, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

export type TimelineWindow = {
  start: number;
  end: number;
  /** Midnight boundaries inside the window, for the column rules. */
  ticks: { at: number; fraction: number; label: string }[];
};

/**
 * The visible span.
 *
 * It starts at the beginning of today rather than at this instant so that work
 * due earlier today is still visible; a window that begins "now" hides the
 * thing most likely to need attention.
 */
export function timelineWindow(
  range: TimelineRange,
  now: Date = new Date()
): TimelineWindow {
  const start = dayjs(now).startOf('day');
  const end =
    range === 'day'
      ? start.add(1, 'day')
      : range === 'week'
        ? start.add(7, 'day')
        : start.add(1, 'month');
  const span = end.valueOf() - start.valueOf();
  const ticks: TimelineWindow['ticks'] = [];

  if (range === 'day') {
    for (let hour = 0; hour < 24; hour += 1) {
      const at = start.add(hour, 'hour');
      ticks.push({
        at: at.valueOf(),
        fraction: (at.valueOf() - start.valueOf()) / span,
        label: hour === 0 ? 'Today' : at.format('h A'),
      });
    }
  } else {
    const days = end.diff(start, 'day');
    const step = range === 'month' ? 7 : 1;
    for (let day = 0; day < days; day += step) {
      const at = start.add(day, 'day');
      ticks.push({
        at: at.valueOf(),
        fraction: (at.valueOf() - start.valueOf()) / span,
        label: day === 0 ? 'Today' : at.format('MMM D'),
      });
    }
  }

  return { start: start.valueOf(), end: end.valueOf(), ticks };
}

export type TimelineAction = {
  id?: string | number;
  name?: string | null;
  status?: string | null;
  priority?: string | null;
  start_date?: Date | string | null;
  target_date?: Date | string | null;
};

export type TimelineBar<T> = {
  action: T;
  /** 0-1 across the window; already clamped to it. */
  left: number;
  width: number;
  /** The bar runs past an edge, so the end is off-screen. */
  clippedStart: boolean;
  clippedEnd: boolean;
  /** Only one date is known, so the bar is a marker rather than a span. */
  isMilestone: boolean;
  overdue: boolean;
};

/** The smallest bar that is still visibly a bar rather than a hairline. */
const MIN_WIDTH = 0.01;

const toMillis = (value: Date | string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

/**
 * Where one action sits in the window, or null if it does not overlap it.
 *
 * A task with only one of the two dates becomes a zero-width milestone rather
 * than being dropped: in practice most actions carry a due date and no start,
 * and dropping them would leave the timeline nearly empty.
 */
export function timelineBarFor<T extends TimelineAction>(
  action: T,
  window: TimelineWindow,
  now: Date = new Date()
): TimelineBar<T> | null {
  const span = window.end - window.start;
  if (span <= 0) return null;

  const startAt = toMillis(action.start_date);
  const endAt = toMillis(action.target_date);
  if (startAt == null && endAt == null) return null;

  const from = startAt ?? endAt!;
  const to = endAt ?? startAt!;
  // Reversed dates are data entry errors, not a reason to render nothing.
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);

  if (hi < window.start || lo > window.end) return null;

  const clampedLo = Math.max(lo, window.start);
  const clampedHi = Math.min(hi, window.end);

  const left = (clampedLo - window.start) / span;
  const width = Math.max((clampedHi - clampedLo) / span, MIN_WIDTH);

  return {
    action,
    left: Math.min(left, 1 - MIN_WIDTH),
    width: Math.min(width, 1 - left),
    clippedStart: lo < window.start,
    clippedEnd: hi > window.end,
    isMilestone: startAt == null || endAt == null || hi === lo,
    overdue: isActionOverdue(action.status, action.target_date, now),
  };
}

/**
 * Bars for everything that overlaps the window, earliest first.
 *
 * Rows are one per action rather than packed into shared lanes. Packing saves
 * vertical space but breaks the thing the view is for: reading down a column to
 * find a task, and lining a row up with the same task in the table.
 */
export function timelineBars<T extends TimelineAction>(
  actions: T[],
  window: TimelineWindow,
  now: Date = new Date()
): TimelineBar<T>[] {
  return actions
    .map((action) => timelineBarFor(action, window, now))
    .filter((bar): bar is TimelineBar<T> => bar != null)
    .sort((a, b) => a.left - b.left || a.width - b.width);
}

/** Where "now" sits in the window, or null when it is outside it. */
export function nowFraction(
  window: TimelineWindow,
  now: Date = new Date()
): number | null {
  const at = now.getTime();
  if (at < window.start || at > window.end) return null;
  return (at - window.start) / (window.end - window.start);
}

/** How many actions the window is hiding, so the count is never a silent lie. */
export function countOutsideWindow<T extends TimelineAction>(
  actions: T[],
  window: TimelineWindow
): { scheduled: number; unscheduled: number } {
  let scheduled = 0;
  let unscheduled = 0;
  actions.forEach((action) => {
    if (!action.start_date && !action.target_date) {
      unscheduled += 1;
      return;
    }
    if (!timelineBarFor(action, window)) scheduled += 1;
  });
  return { scheduled, unscheduled };
}

export function describeHiddenActions(hidden: {
  scheduled: number;
  unscheduled: number;
}): string | null {
  const parts: string[] = [];
  if (hidden.scheduled > 0) {
    parts.push(`${hidden.scheduled} outside this window`);
  }
  if (hidden.unscheduled > 0) {
    parts.push(`${hidden.unscheduled} with no dates`);
  }
  return parts.length > 0 ? `${parts.join(', ')} not shown` : null;
}

export const UNASSIGNED_LANE_ID = '__unassigned__';
export const UNASSIGNED_LANE_LABEL = 'Unassigned';

export type TimelineLanePosition = { id: string; name: string };

export type TimelineRosterMember = {
  userId: string;
  incidentPositions: { positionId: string; positionName?: string }[];
};

export type TimelineLaneAction = TimelineAction & {
  assignee_kind?: string | null;
  assignee_id?: string | null;
  poc?: string | null;
  responsible_individual_ids?: string[] | null;
};

export type TimelineLane<T> = {
  id: string;
  label: string;
  /** Bars packed into the fewest rows where none overlap in time. */
  rows: TimelineBar<T>[][];
  count: number;
};

/**
 * Position ids an action belongs to on the roster-lane view.
 *
 * A position assignee owns the task outright. Otherwise the task follows the
 * named people: responsible individuals first, then the user assignee, mapped
 * to whatever seats they hold on the roster.
 */
export function actionLanePositionIds(
  action: TimelineLaneAction,
  roster: readonly TimelineRosterMember[]
): string[] {
  if (action.assignee_kind === 'position') {
    return action.assignee_id ? [action.assignee_id] : [];
  }
  const userIds = new Set<string>(
    (action.responsible_individual_ids ?? []).filter(Boolean)
  );
  if (userIds.size === 0) {
    const userId =
      action.assignee_kind === 'user' || !action.assignee_kind
        ? (action.assignee_id ?? action.poc)
        : null;
    if (userId) userIds.add(userId);
  }
  const ids = new Set<string>();
  roster.forEach((member) => {
    if (!userIds.has(member.userId)) return;
    member.incidentPositions.forEach((position) =>
      ids.add(position.positionId)
    );
  });
  return [...ids];
}

/** Greedy first-fit: a bar joins the first row it does not overlap. */
export function packLaneRows<T>(bars: TimelineBar<T>[]): TimelineBar<T>[][] {
  const rows: TimelineBar<T>[][] = [];
  const ends: number[] = [];
  [...bars]
    .sort((a, b) => a.left - b.left || a.width - b.width)
    .forEach((bar) => {
      const index = ends.findIndex((end) => bar.left >= end);
      if (index < 0) {
        rows.push([bar]);
        ends.push(bar.left + bar.width);
        return;
      }
      rows[index].push(bar);
      ends[index] = bar.left + bar.width;
    });
  return rows;
}

/**
 * One lane per roster position, with the tasks that sit in the window placed by
 * assignee position. Tasks with no resolvable position land in a trailing
 * "Unassigned" lane. A task held by two seats shows in both lanes. Lanes with
 * nothing in the window are dropped unless `includeEmpty` is set.
 */
export function timelinePositionLanes<T extends TimelineLaneAction>(
  actions: T[],
  window: TimelineWindow,
  positions: readonly TimelineLanePosition[],
  roster: readonly TimelineRosterMember[],
  options: { now?: Date; includeEmpty?: boolean } = {}
): TimelineLane<T>[] {
  const { now = new Date(), includeEmpty = false } = options;
  const known = new Set(positions.map((position) => position.id));
  const barsByLane = new Map<string, TimelineBar<T>[]>();

  actions.forEach((action) => {
    const bar = timelineBarFor(action, window, now);
    if (!bar) return;
    const laneIds = actionLanePositionIds(action, roster).filter((id) =>
      known.has(id)
    );
    const targets = laneIds.length > 0 ? laneIds : [UNASSIGNED_LANE_ID];
    targets.forEach((id) => {
      const list = barsByLane.get(id) ?? [];
      list.push(bar);
      barsByLane.set(id, list);
    });
  });

  const lanes: TimelineLane<T>[] = [...positions]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((position) => {
      const bars = barsByLane.get(position.id) ?? [];
      return {
        id: position.id,
        label: position.name,
        rows: packLaneRows(bars),
        count: bars.length,
      };
    });
  const unassigned = barsByLane.get(UNASSIGNED_LANE_ID) ?? [];
  lanes.push({
    id: UNASSIGNED_LANE_ID,
    label: UNASSIGNED_LANE_LABEL,
    rows: packLaneRows(unassigned),
    count: unassigned.length,
  });

  return includeEmpty ? lanes : lanes.filter((lane) => lane.count > 0);
}
